/**
 * ================================================================
 * Service de Facturation par Phases (Updated Logic)
 * ================================================================
 * Billing is based on cumulative aligner count per user (dentist).
 * Each time a batch is launched:
 *   1. Calculate batch aligners from model ranges
 *   2. new_total = user.total_aligners + batch_aligners
 *   3. Find which phase range new_total falls into
 *   4. If that phase is not yet invoiced → generate invoice
 *   5. Update user.total_aligners = new_total
 */

import { supabase } from '../lib/supabase';
import caseServicesService from './caseServicesService';

class PhaseBillingService {

  /**
   * Calculate aligners for a single batch from its model ranges
   * @param {Object} batch - Batch object with model_up/low_from/to fields
   * @returns {number} Number of aligners in this batch
   */
  calculateBatchAligners(batch) {
    let upperCount = 0;
    let lowerCount = 0;

    if (
      batch.model_up_to !== null && batch.model_up_to !== undefined &&
      batch.model_up_from !== null && batch.model_up_from !== undefined &&
      batch.model_up_to >= batch.model_up_from
    ) {
      upperCount = batch.model_up_to - batch.model_up_from ;
    }

    if (
      batch.model_low_to !== null && batch.model_low_to !== undefined &&
      batch.model_low_from !== null && batch.model_low_from !== undefined &&
      batch.model_low_to >= batch.model_low_from
    ) {
      lowerCount = batch.model_low_to - batch.model_low_from ;
    }

    return upperCount + lowerCount;
  }

  /**
   * Get the user's current total_aligners from user_profiles
   * @param {string} userId - User profile ID (dentist)
   * @returns {Promise<number>} Current total aligners
   */
  async getUserTotalAligners(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('total_aligners')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data?.total_aligners ?? 0;
    } catch (error) {
      console.error('Error fetching user total aligners:', error);
      return 0;
    }
  }

  /**
   * Update the user's total_aligners in user_profiles
   * @param {string} userId - User profile ID
   * @param {number} newTotal - New total aligner count
   * @returns {Promise<boolean>} Success
   */
  async updateUserTotalAligners(userId, newTotal) {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          total_aligners: newTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
      console.log(`✓ Updated user ${userId} total_aligners to ${newTotal}`);
      return true;
    } catch (error) {
      console.error('Error updating user total aligners:', error);
      return false;
    }
  }

  /**
   * Find which billing phase a given aligner count falls into
   * @param {string} packageId - Package ID
   * @param {number} totalAligners - Total aligner count to check
   * @returns {Promise<Object|null>} Matching phase config or null
   */
  async findPhaseForAlignerCount(packageId, totalAligners) {
    try {
      const { data, error } = await supabase
        .from('package_billing_phases')
        .select('*')
        .eq('package_id', packageId)
        .eq('is_active', true)
        .lte('aligner_range_from', totalAligners)
        .gte('aligner_range_to', totalAligners)
        .order('phase_number', { ascending: true })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data || null;
    } catch (error) {
      console.error('Error finding phase for aligner count:', error);
      return null;
    }
  }

  /**
   * Check if a specific phase has already been invoiced for a user
   * @param {string} caseId - Case ID (we still track per case)
   * @param {number} phaseNumber - Phase number
   * @returns {Promise<boolean>} true if already invoiced
   */
  async isPhaseAlreadyBilled(caseId, phaseNumber) {
    try {
      const { data, error } = await supabase
        .from('case_phase_invoices')
        .select('id')
        .eq('case_id', caseId)
        .eq('phase_number', phaseNumber)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking if phase already billed:', error);
      return false;
    }
  }

  /**
   * Get all billed phases for a case
   * @param {string} caseId - Case ID
   * @returns {Promise<Array>} List of billed phases
   */
  async getBilledPhases(caseId) {
    try {
      const { data, error } = await supabase
        .from('case_phase_invoices')
        .select(`
          *,
          billing_phase:package_billing_phases(*),
          invoice:invoices(*)
        `)
        .eq('case_id', caseId)
        .order('phase_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching billed phases:', error);
      return [];
    }
  }

  /**
   * Create a phase invoice record
   * @param {Object} params - Phase invoice parameters
   * @returns {Promise<Object>} Created record
   */
  async createPhaseInvoiceRecord(params) {
    const {
      caseId,
      billingPhaseId,
      phaseNumber,
      phaseName,
      alignersProduced,
      phasePrice,
      productionBatchId,
      notes
    } = params;

    try {
      const { data, error } = await supabase
        .from('case_phase_invoices')
        .insert({
          case_id: caseId,
          billing_phase_id: billingPhaseId,
          phase_number: phaseNumber,
          phase_name: phaseName,
          aligners_produced_at_billing: alignersProduced,
          phase_price: phasePrice,
          status: 'pending',
          production_batch_id: productionBatchId,
          notes: notes || null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating phase invoice record:', error);
      throw error;
    }
  }

  /**
   * Generate an actual invoice for a phase
   * @param {string} phaseInvoiceId - ID of the case_phase_invoices record
   * @returns {Promise<Object>} Created invoice
   */
  async generateInvoiceForPhase(phaseInvoiceId) {
    try {
       const { data: phaseInvoice, error: phaseError } = await supabase
      .from('case_phase_invoices')
      .select(`
        *,
        case:cases(
          id,
          case_number,
          package,
          services_invoiced_at,
          patient:patients(name),
          dentist:user_profiles!cases_user_profile_id_fkey(
            id,
            full_name,
            email,
            total_aligners,
            country:countries(id, currency_code)
          )
        )
      `)
      .eq('id', phaseInvoiceId)
      .single();

      if (phaseError) throw phaseError;

      const caseId = phaseInvoice.case.id;
      const phasePrice = Number(phaseInvoice.phase_price) || 0;
      const includeCaseServices = !phaseInvoice.case.services_invoiced_at;

      let servicesTotal = 0;
      let serviceRows = [];
      if (includeCaseServices) {
        const { total, rows } = await caseServicesService.getTotalForCase(caseId);
        servicesTotal = total;
        serviceRows = rows;
      }

      const subtotal = phasePrice + servicesTotal;
      const serviceLabels = serviceRows.map((r) => r.serviceName).join(', ');
      let notes = `Facturation ${phaseInvoice.phase_name} - Cas ${phaseInvoice.case.case_number} - Patient ${phaseInvoice.case.patient.name} - ${phaseInvoice.aligners_produced_at_billing} aligneurs produits (total cumulé)`;
      if (serviceRows.length > 0) {
        notes += ` — Services commande : ${serviceLabels}`;
      }

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const rand = Math.floor(Math.random() * 9000) + 1000;
      const invoiceNumber = `INV-${year}${month}-${rand}`;

      const invoicePayload = {
        invoice_number: invoiceNumber,
        client_name: phaseInvoice.case.dentist.full_name,
        client_email: phaseInvoice.case.dentist.email,
        client_id: phaseInvoice.case.dentist.id,
        country_id: phaseInvoice.case.dentist.country?.id,
        package_id: phaseInvoice.case.package || null,
        status: 'pending',
        issue_date: now.toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        subtotal,
        tax_percentage: 0,
        tax_amount: 0,
        total_amount: subtotal,
        amount_paid: 0,
        balance_due: subtotal,
        notes,
      };

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert(invoicePayload)
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      const lineItems = [
        {
          invoice_id: invoice.id,
          line_type: 'phase',
          service_id: null,
          description: phaseInvoice.phase_name,
          quantity: 1,
          unit_price: phasePrice,
          line_total: phasePrice,
          sort_order: 0,
        },
        ...serviceRows.map((row, index) => ({
          invoice_id: invoice.id,
          line_type: 'service',
          service_id: row.serviceId,
          description: row.serviceName,
          quantity: row.quantity,
          unit_price: row.unitPrice,
          line_total: row.lineTotal,
          sort_order: index + 1,
        })),
      ];

      const { error: linesError } = await supabase.from('invoice_line_items').insert(lineItems);
      if (linesError) {
        console.error('invoice_line_items insert:', linesError);
      }

      if (serviceRows.length > 0) {
        await supabase
          .from('cases')
          .update({ services_invoiced_at: now.toISOString(), updated_at: now.toISOString() })
          .eq('id', caseId);
      }

      const { error: updateError } = await supabase
        .from('case_phase_invoices')
        .update({
          invoice_id: invoice.id,
          status: 'invoiced',
          billing_date: now.toISOString()
        })
        .eq('id', phaseInvoiceId);

      if (updateError) throw updateError;

      return invoice;
    } catch (error) {
      console.error('Error generating invoice for phase:', error);
      throw error;
    }
  }

  /**
   * ════════════════════════════════════════════════════════════════
   * MAIN METHOD: Check and trigger billing when launching a batch
   * ════════════════════════════════════════════════════════════════
   * 
   * Logic:
   *   1. Get batch + case + dentist + package info
   *   2. Calculate batch aligners from model ranges
   *   3. new_total = user.total_aligners + batch_aligners
   *   4. Find which phase range new_total falls into
   *   5. If phase not yet invoiced → create invoice
   *   6. Update user.total_aligners = new_total
   * 
   * @param {string} batchId - Production batch ID
   * @returns {Promise<Object>} Result with success/message/invoice info
   */
async checkAndTriggerBillingForBatch(batchId) {
  try {
    // ── Step 1: Fetch batch + case + package ──
    const { data: batch, error: batchError } = await supabase
      .from('production_batches')
      .select(`
        id, case_id, batch_number,
        model_up_from, model_up_to,
        model_low_from, model_low_to,
        case:cases(
          id, case_number, package,
          dentist:user_profiles!cases_user_profile_id_fkey(
            id, full_name, email,
            country:countries(id, currency_code)
          )
        )
      `)
      .eq('id', batchId)
      .single();

    if (batchError) throw batchError;
    if (!batch.case_id) return { success: false, message: 'Batch non lié à un cas' };

    const packageId = batch.case?.package;
    if (!packageId) return { success: false, message: "Ce cas n'a pas de package assigné" };

    // ── Step 2: Calculate newTotal (all batches of this case including current) ──
    const { data: allBatches, error: allBatchesError } = await supabase
      .from('production_batches')
      .select('model_up_from, model_up_to, model_low_from, model_low_to')
      .eq('case_id', batch.case_id);

    if (allBatchesError) throw allBatchesError;

    const newTotal = (allBatches || []).reduce(
      (sum, b) => sum + this.calculateBatchAligners(b), 0
    );

    console.log(`✓ Case ${batch.case.case_number} total aligners: ${newTotal}`);

    // ── Step 3: Find which phase newTotal lands in (range_from ≤ newTotal ≤ range_to) ──
    const { data: currentPhase, error: currentPhaseError } = await supabase
      .from('package_billing_phases')
      .select('*')
      .eq('package_id', packageId)
      .eq('is_active', true)
      .lte('aligner_range_from', newTotal)
      .gte('aligner_range_to', newTotal)
      .order('phase_number', { ascending: true })
      .limit(1)
      .single();

    if (currentPhaseError && currentPhaseError.code !== 'PGRST116') throw currentPhaseError;

    if (!currentPhase) {
      return {
        success: true,
        message: `Total aligneurs: ${newTotal} — hors de toute plage de phase`,
        totalAligners: newTotal,
        invoicesGenerated: [],
        shouldGenerateInvoice: false
      };
    }

    console.log(`✓ newTotal ${newTotal} lands in phase ${currentPhase.phase_number} "${currentPhase.phase_name}"`);

    // ── Step 4: Fetch ALL phases from 1 up to currentPhase.phase_number ──
    const { data: phasesToBill, error: phasesError } = await supabase
      .from('package_billing_phases')
      .select('*')
      .eq('package_id', packageId)
      .eq('is_active', true)
      .lte('phase_number', currentPhase.phase_number)  // phases 1 → N
      .order('phase_number', { ascending: true });

    if (phasesError) throw phasesError;

    // ── Step 5: For each phase 1→N, invoice only if not already billed ──
    const invoicesGenerated = [];
    const skippedPhases = [];

    for (const phase of phasesToBill) {
      const alreadyBilled = await this.isPhaseAlreadyBilled(
        batch.case_id,
        phase.phase_number
      );

      if (alreadyBilled) {
        console.log(`↷ Phase ${phase.phase_number} "${phase.phase_name}" already billed — skipping`);
        skippedPhases.push(phase.phase_name);
        continue;
      }

      // Create phase invoice record
      const phaseInvoiceRecord = await this.createPhaseInvoiceRecord({
        caseId:            batch.case_id,
        billingPhaseId:    phase.id,
        phaseNumber:       phase.phase_number,
        phaseName:         phase.phase_name,
        alignersProduced:  newTotal,
        phasePrice:        phase.phase_price,
        productionBatchId: batch.id,
        notes: `Auto — total cas: ${newTotal}, batch: ${batch.batch_number}`
      });

      // Generate the actual invoice immediately
      const invoice = await this.generateInvoiceForPhase(phaseInvoiceRecord.id);

      console.log(`✓ Invoice generated for phase ${phase.phase_number} "${phase.phase_name}"`);
      invoicesGenerated.push({ phase, phaseInvoiceRecord, invoice });
    }

    return {
      success: true,
      message: invoicesGenerated.length > 0
        ? `${invoicesGenerated.length} facture(s) générée(s) — phases: ${invoicesGenerated.map(i => i.phase.phase_name).join(', ')}`
        : `Toutes les phases déjà facturées pour ce cas`,
      totalAligners: newTotal,
      currentPhase,
      invoicesGenerated,
      skippedPhases,
      shouldGenerateInvoice: invoicesGenerated.length > 0
    };

  } catch (error) {
    console.error('Error checking billing for batch:', error);
    return { success: false, message: error.message, error };
  }
}

  /**
   * Get billing phases config for a package
   */
  async getBillingPhasesConfig(packageId) {
    try {
      const { data, error } = await supabase
        .from('package_billing_phases')
        .select('*')
        .eq('package_id', packageId)
        .eq('is_active', true)
        .order('phase_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching billing phases config:', error);
      return [];
    }
  }

  /**
   * Get case billing summary
   */
  async getCaseBillingSummary(caseId) {
    try {
      const { data, error } = await supabase
        .from('v_case_billing_summary')
        .select('*')
        .eq('case_id', caseId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching case billing summary:', error);
      return null;
    }
  }

  /**
   * Update package billing phases configuration
   */
  async updatePackageBillingPhases(packageId, phasesConfig) {
    try {
      const { data: existingPhases, error: fetchError } = await supabase
        .from('package_billing_phases')
        .select('id, phase_number')
        .eq('package_id', packageId);

      if (fetchError) throw fetchError;

      const newPhaseNumbers = phasesConfig.map((_, index) => index + 1);
      const phasesToDelete = existingPhases?.filter(
        (phase) => !newPhaseNumbers.includes(phase.phase_number)
      ) || [];

      if (phasesToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('package_billing_phases')
          .delete()
          .in('id', phasesToDelete.map(p => p.id));

        if (deleteError) throw deleteError;
      }

      for (let index = 0; index < phasesConfig.length; index++) {
        const phase = phasesConfig[index];
        const phaseNumber = index + 1;

        const { error: upsertError } = await supabase
          .from('package_billing_phases')
          .upsert(
            {
              package_id: packageId,
              phase_number: phaseNumber,
              phase_name: phase.phaseName,
              aligner_range_from: phase.rangeFrom,
              aligner_range_to: phase.rangeTo,
              phase_price: phase.price,
              is_active: true,
              updated_at: new Date().toISOString()
            },
            { onConflict: 'package_id,phase_number' }
          );

        if (upsertError) throw upsertError;
      }

      return true;
    } catch (error) {
      console.error('Error updating package billing phases:', error);
      throw error;
    }
  }

  /**
 * Get total aligners already produced for a specific CASE
 * (sum of all previous completed + in_progress batches for this case)
 */
async getCaseTotalAligners(caseId) {
  try {
    const { data, error } = await supabase
      .from('production_batches')
      .select('model_up_from, model_up_to, model_low_from, model_low_to')
      .eq('case_id', caseId);

    if (error) throw error;

    return (data || []).reduce((sum, b) => sum + this.calculateBatchAligners(b), 0);
  } catch (error) {
    console.error('Error fetching case total aligners:', error);
    return 0;
  }
}


  /**
   * Calcule le nombre total d'aligneurs produits pour un cas
   * @param {string} caseId - ID du cas
   * @param {number} phaseNumber - Numéro de phase (optionnel, pour filtrer par phase)
   * @returns {Promise<number>} Nombre total d'aligneurs
   */
  async calculateTotalAligners(caseId, phaseNumber = null) {
    try {
      let query = supabase
        .from('production_batches')
        .select('model_up_from, model_up_to, model_low_from, model_low_to, current_phase, status')
        .eq('case_id', caseId)
        .eq('status', 'completed'); // Seuls les batches complétés comptent pour la facturation

      // Si une phase spécifique est demandée, filtrer par phase
      if (phaseNumber !== null) {
        query = query.eq('current_phase', phaseNumber);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Calculer le total des aligneurs
      const total = (data || []).reduce((sum, batch) => {
        let upperCount = 0;
        let lowerCount = 0;

        // Calcul des aligneurs supérieurs
        if (
          batch.model_up_to !== null &&
          batch.model_up_from !== null &&
          batch.model_up_to >= batch.model_up_from
        ) {
          upperCount = batch.model_up_to - batch.model_up_from ;
        }

        // Calcul des aligneurs inférieurs
        if (
          batch.model_low_to !== null &&
          batch.model_low_from !== null &&
          batch.model_low_to >= batch.model_low_from
        ) {
          lowerCount = batch.model_low_to - batch.model_low_from;
        }

        return sum + upperCount + lowerCount;
      }, 0);

      console.log(
        `✓ Aligners calculated for case ${caseId}${
          phaseNumber ? ` (phase ${phaseNumber})` : ''
        }: ${total}`
      );

      return total;
    } catch (error) {
      console.error('Error calculating total aligners:', error);
      return 0;
    }
  }
}

export default new PhaseBillingService();